'use client';

import {
  isChunkLoadError,
  recoverFromChunkError,
} from '@/layers/presentation/utils/chunkErrorRecovery';
import Link from 'next/link';
import { useEffect } from 'react';

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

// Route-level error boundary for the authenticated `/users` subtree. This is the
// only thing that catches a failure of the dynamically-imported AppProviders
// (and Amplify). After a redeploy, stale lazy chunks 404 and the dynamic
// import() rejects here — we self-heal with a one-time reload instead of leaving
// a blank white screen (bug B11).
export default function UsersError({ error, reset }: ErrorPageProps) {
  const isChunk = isChunkLoadError(error);

  useEffect(() => {
    // Self-heal stale-chunk failures with a single guarded reload.
    if (recoverFromChunkError(error)) return;

    console.error('Users route error:', error);
  }, [error]);

  // While the reload is in flight, show a neutral "reloading" message rather
  // than the generic error UI (the reload was already triggered above).
  if (isChunk) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <h2 className="text-xl font-semibold text-foreground mb-2">
          Updating to the latest version…
        </h2>
        <p className="text-default-500 mb-6 max-w-md">
          A new version is available. Reloading now. If this page does not refresh automatically,
          please reload manually.
        </p>
        <button
          type="button"
          className="rh-btn-black-lg text-white"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <h2 className="text-2xl font-semibold text-foreground mb-4">Something went wrong</h2>
      <p className="text-default-500 mb-8 max-w-2xl">
        We apologize for the inconvenience. Please try again, and if the problem persists, contact
        our support team.
      </p>
      <div className="flex gap-4">
        <button type="button" className="rh-btn-black-lg text-white" onClick={reset}>
          Try Again
        </button>
        <Link
          href="/"
          className="rh-btn-black-lg bg-white !text-black border border-black hover:bg-gray-50"
        >
          Return to Home
        </Link>
      </div>
    </div>
  );
}
